import { type FC, useEffect, useRef, useState } from 'react'
import { Chart } from 'react-chartjs-2'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartDataset as ChartJSDataset,
  type ChartOptions,
  type LegendItem,
  type TooltipModel,
} from 'chart.js'
import clsx from 'clsx'
import { combineMultiChartDatasets } from './utils/combineMultiChartDatasets'
import { createScales } from './utils/createScales'
import type { MultiChartProps, ChartType } from './types'
import { EmptyState, Spinner } from '@sylveraio/design-system'
import isEmpty from 'lodash/isEmpty'
import type { ScatterDataPoint } from '../types'
import {
  CUSTOM_BORDER_PLUGIN,
  CUSTOM_INTERSECTING_LINE_PLUGIN,
  DEFAULT_COLORS,
  DEFAULT_HOVER_OPTIONS,
  UI_STRINGS,
} from './constants'
import { MultiChartLegendItem } from './MultiChartLegendItem'
import { MultiChartTooltip } from './MultiChartTooltip'
import FileX02 from '@sylveraio/untitled-ui-icons-react/build/esm/FileX02'

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  Filler,
)

if (typeof window !== 'undefined') {
  ;(async () => {
    const { default: zoomPlugin } = await import('chartjs-plugin-zoom')
    ChartJS.register(zoomPlugin)
  })()
}

export const MultiChart: FC<MultiChartProps> = ({
  className,
  data,
  enableFiltering,
  hideGridLines = true,
  isLoading = false,
  labels,
  legendPosition = 'right',
  testId = 'bar-chart',
  tooltipOptions,
  xAxisOptions,
  y1AxisOptions,
  yAxisOptions,
}) => {
  const chartRef = useRef<ChartJS<ChartType, ScatterDataPoint[]> | null>(null)
  const [chartData, setChartData] = useState<{
    labels?: string[]
    datasets: ChartJSDataset<ChartType, ScatterDataPoint[]>[]
  }>({
    datasets: [],
  })
  const [chartOptions, setChartOptions] = useState<ChartOptions<ChartType>>({})
  const [legendItems, setLegendItems] = useState<LegendItem[] | undefined>([])
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: TooltipModel<ChartType>['caretX']
    y: TooltipModel<ChartType>['caretY']
    xAlign: TooltipModel<ChartType>['xAlign']
  }>({
    x: 0,
    y: 0,
    xAlign: 'left',
  })
  const [tooltipOpacity, setTooltipOpacity] = useState(0)
  const [tooltipContent, setTooltipContent] = useState<{
    title: TooltipModel<ChartType>['title'][number]
    dataPoints: {
      datasetColor: string
      datasetLabel: TooltipModel<ChartType>['dataPoints'][number]['dataset']['label']
      value: TooltipModel<ChartType>['dataPoints'][number]['parsed']['y']
    }[]
  }>({
    title: '',
    dataPoints: [],
  })
  const [hasDecimalYAxisTicks, setHasDecimalYAxisTicks] = useState(false)

  const hasData = !isEmpty(chartData.datasets)

  useEffect(() => {
    const datasets = combineMultiChartDatasets(data, DEFAULT_COLORS)

    setChartData({
      labels,
      datasets,
    })

    setChartOptions({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            setTooltipPosition({
              x: context.tooltip.caretX || 0,
              y: context.chart.chartArea.top,
              xAlign: context.tooltip.xAlign,
            })
            setTooltipContent({
              title: context.tooltip.title?.[0],
              dataPoints: context.tooltip.dataPoints?.map((dataPoint) => ({
                datasetColor:
                  dataPoint.dataset.pointBackgroundColor?.toString() || '',
                datasetLabel: dataPoint.dataset.label,
                value: dataPoint.parsed.y,
              })),
            })
            setTooltipOpacity(context.tooltip.opacity)
          },
          position: 'average',
          ...DEFAULT_HOVER_OPTIONS,
          ...tooltipOptions,
        },
        datalabels: {
          display: false,
        },
      },
      hover: {
        ...DEFAULT_HOVER_OPTIONS,
      },
      scales: {
        ...createScales(
          [xAxisOptions, yAxisOptions, y1AxisOptions],
          !hideGridLines,
          labels,
          hasDecimalYAxisTicks,
        ),
      },
    })
  }, [data, hasDecimalYAxisTicks])

  useEffect(() => {
    setLegendItems(chartRef.current?.legend?.legendItems)
    if (chartRef.current) {
      chartRef.current.update()
    }
  }, [labels, chartData, chartOptions])

  useEffect(() => {
    const hasDecimalYAxisTicks = chartRef.current?.scales['y']?.ticks?.some(
      (tick) => tick.value % 1 !== 0,
    )
    setHasDecimalYAxisTicks(hasDecimalYAxisTicks || false)
  }, [chartRef.current?.scales['y']?.ticks])

  function handleLegendItemClick(datasetIndex: LegendItem['datasetIndex']) {
    if (typeof datasetIndex === 'undefined') return

    setChartData((prevData) => ({
      ...prevData,
      datasets: prevData.datasets.map((dataset, index) =>
        index === datasetIndex
          ? { ...dataset, hidden: !dataset.hidden }
          : dataset,
      ),
    }))
  }

  return (
    <div
      data-testid={`${testId}-wrapper`}
      className={clsx('relative w-full', className)}
    >
      {(!hasData || isLoading) && (
        <div className="absolute flex h-full w-full items-center justify-center">
          {isLoading ? (
            <Spinner className="h-12 w-12" />
          ) : (
            <EmptyState
              withContainer={false}
              icon={<FileX02 className="h-8 w-8" />}
              title={UI_STRINGS.noDataTitle}
              subtitle={UI_STRINGS.noDataDescription}
            />
          )}
        </div>
      )}
      <div
        className={clsx('flex flex-1', {
          'opacity-30': !hasData || isLoading,
          'items-center space-x-1':
            legendPosition === 'right' || legendPosition === 'left',
          'space-y-1': legendPosition === 'bottom' || legendPosition === 'top',
          'flex-row': legendPosition === 'right',
          'flex-row-reverse': legendPosition === 'left',
          'flex-col': legendPosition === 'bottom',
          'flex-col-reverse': legendPosition === 'top',
        })}
      >
        <div className="relative flex-1 overflow-hidden">
          <Chart
            data-testid={testId}
            ref={chartRef}
            options={chartOptions}
            data={chartData}
            plugins={[CUSTOM_BORDER_PLUGIN, CUSTOM_INTERSECTING_LINE_PLUGIN]}
            // The 'type' prop is required by Chart.js but is arbitrary here
            // because we are implementing a 'mixed' chart, which displays
            // multiple types (e.g., 'bar' and 'line') on the same chart.
            type="bar"
          />
          <MultiChartTooltip
            {...tooltipPosition}
            {...tooltipContent}
            opacity={tooltipOpacity}
            tickFormat={yAxisOptions?.tickFormat}
          />
        </div>

        <div
          className={clsx('flex justify-center shrink-0', {
            'flex-col space-y-2':
              legendPosition === 'right' || legendPosition === 'left',
            'flex-row space-x-10 w-full':
              legendPosition === 'bottom' || legendPosition === 'top',
          })}
        >
          {legendPosition !== 'none' &&
            legendItems?.map((item) => (
              <MultiChartLegendItem
                key={`legend-${item.text}`}
                legendItem={item}
                enableFiltering={enableFiltering}
                onClick={handleLegendItemClick}
              />
            ))}
        </div>
      </div>
    </div>
  )
}

export default MultiChart
